// Sep 2026 — date-range helpers for the Dispatch Lots screen. The
// shared DateRangePicker emits { preset, from?, to? }; these helpers
// resolve that state into explicit YYYY-MM-DD bounds that the backend
// `/dispatch-lots-v2` + `/summary` endpoints filter on. The shared
// picker itself is untouched — the "All dates" toggle lives only on
// the Dispatch Lots screen.
//
// NOTE: ESM exports — the production webpack build rejects named ESM
// imports from CommonJS modules. Jest coverage runs via `craco test`
// (babel transforms this file), NOT plain `npx jest`.

const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Default: actual first → last day of the current calendar month.
const defaultMonthRange = () => {
  const now = new Date();
  return {
    preset: "custom",
    from: isoDay(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: isoDay(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

// Resolve picker state → { from, to } (YYYY-MM-DD), or null when the
// "All dates" toggle is on (no date params are sent to the API).
const resolveRangeBounds = (range, allDates) => {
  if (allDates || !range) return null;
  if (range.preset === "custom") {
    return range.from && range.to ? { from: range.from, to: range.to } : null;
  }
  const today = new Date();
  const daysAgo = (n) => {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    d.setDate(d.getDate() - n);
    return d;
  };
  const monthStart = (off) => new Date(today.getFullYear(), today.getMonth() + off, 1);
  const monthEnd = (off) => new Date(today.getFullYear(), today.getMonth() + off + 1, 0);
  const quarterStart = (off) =>
    new Date(today.getFullYear(), (Math.floor(today.getMonth() / 3) + off) * 3, 1);
  const quarterEnd = (off) =>
    new Date(today.getFullYear(), (Math.floor(today.getMonth() / 3) + off) * 3 + 3, 0);
  switch (range.preset) {
    case "today":
      return { from: isoDay(today), to: isoDay(today) };
    case "last_7_days":
      return { from: isoDay(daysAgo(6)), to: isoDay(today) };
    case "last_30_days":
      return { from: isoDay(daysAgo(29)), to: isoDay(today) };
    case "current_month":
      return { from: isoDay(monthStart(0)), to: isoDay(monthEnd(0)) };
    case "last_month":
      return { from: isoDay(monthStart(-1)), to: isoDay(monthEnd(-1)) };
    case "current_quarter":
      return { from: isoDay(quarterStart(0)), to: isoDay(quarterEnd(0)) };
    case "last_quarter":
      return { from: isoDay(quarterStart(-1)), to: isoDay(quarterEnd(-1)) };
    case "current_year":
      return {
        from: isoDay(new Date(today.getFullYear(), 0, 1)),
        to: isoDay(new Date(today.getFullYear(), 11, 31)),
      };
    case "last_year":
      return {
        from: isoDay(new Date(today.getFullYear() - 1, 0, 1)),
        to: isoDay(new Date(today.getFullYear() - 1, 11, 31)),
      };
    default:
      return null;
  }
};

export { isoDay, defaultMonthRange, resolveRangeBounds };
